/**
 * Workflow Trigger Service
 *
 * Service centralizzato che ascolta tutti gli eventi domain
 * e triggera i workflow appropriati tramite workflowExecutionService.
 *
 * Questo service disaccoppia completamente gli store dalla logica workflow:
 * - Store emettono eventi generici
 * - WorkflowTriggerService trasforma eventi in trigger workflow
 * - workflowExecutionService esegue i workflow
 *
 * Usage:
 * ```typescript
 * import { workflowTriggerService } from './workflowTriggerService';
 *
 * // All'avvio dell'app (in main.tsx o App.tsx)
 * workflowTriggerService.init();
 * ```
 */
import { eventBus } from './eventBus';
import { workflowExecutionService } from '../services/workflowExecutionService';
import { supabase } from '../supabase';
import type { DomainEvent } from './types';

class WorkflowTriggerService {
  private initialized = false;

  /**
   * Initialize all event listeners
   * DEVE essere chiamato UNA VOLTA all'avvio dell'applicazione
   */
  init(): void {
    if (this.initialized) {
      console.warn('[WorkflowTriggerService] ⚠️ Already initialized');
      return;
    }

    console.log('[WorkflowTriggerService] 🚀 Initializing workflow triggers...');

    // Register all event handlers
    this.registerLeadEvents();
    this.registerClientEvents();
    this.registerDealEvents();
    this.registerNewsletterEvents();
    this.registerTagAndInterestEvents();
    this.registerCrossModuleEvents();

    this.initialized = true;

    const listenerCount = eventBus.getListenerCount();
    console.log(
      `[WorkflowTriggerService] ✅ Initialized with ${listenerCount} listeners`
    );
    console.log(
      `[WorkflowTriggerService] 📋 Registered events:`,
      eventBus.getRegisteredEvents()
    );
  }

  /**
   * Cleanup all listeners
   * Utile per testing o hot module replacement
   */
  destroy(): void {
    eventBus.removeAllListeners();
    this.initialized = false;
    console.log('[WorkflowTriggerService] 🗑️ Destroyed all listeners');
  }

  /**
   * Check if service is initialized
   */
  isInitialized(): boolean {
    return this.initialized;
  }

  // ============================================================
  // LEAD EVENT HANDLERS
  // ============================================================

  private registerLeadEvents() {
    // Lead Created
    eventBus.onEvent('lead:created', async (payload) => {
      try {
        console.log('[WorkflowTriggerService] 🔔 Triggering lead:created workflow', {
          leadId: payload.leadId,
          pipelineId: payload.pipelineId,
        });

        await workflowExecutionService.triggerWorkflows({
          entityType: 'lead',
          triggerType: 'lead_created',
          entityId: payload.leadId,
          triggerConfig: {
            pipeline_id: payload.pipelineId,
            pipeline_stage_id: payload.pipelineStageId,
            lead_source: payload.leadSource,
            ad_platform: payload.adPlatform,
            assigned_to: payload.assignedTo,
            interest_category_ids: payload.interestCategoryIds,
          },
          triggerData: {
            business_name: payload.businessName,
            contact_name: payload.contactName,
            email: payload.email,
            phone: payload.phone,
            lead_source: payload.leadSource,
            stato_cliente: payload.statoCliente,
            assigned_to: payload.assignedTo,
          },
        });
      } catch (error) {
        console.error(
          '[WorkflowTriggerService] ❌ Error in lead:created handler:',
          error
        );
        // Non far fallire l'operazione principale se workflow fallisce
      }
    });

    // Lead State Changed
    eventBus.onEvent('lead:state_changed', async (payload) => {
      try {
        console.log(
          '[WorkflowTriggerService] 🔔 Triggering lead:state_changed workflow',
          {
            leadId: payload.leadId,
            fromStateId: payload.fromStateId,
            toStateId: payload.toStateId,
          }
        );

        await workflowExecutionService.triggerWorkflows({
          entityType: 'lead',
          triggerType: 'lead_state_changed',
          entityId: payload.leadId,
          triggerConfig: {
            pipeline_id: payload.pipelineId,
            from_state_id: payload.fromStateId,
            to_state_id: payload.toStateId,
          },
          triggerData: {
            business_name: payload.businessName,
          },
        });
      } catch (error) {
        console.error(
          '[WorkflowTriggerService] ❌ Error in lead:state_changed handler:',
          error
        );
      }
    });

    // Lead Pipeline Changed
    eventBus.onEvent('lead:pipeline_changed', async (payload) => {
      try {
        console.log(
          '[WorkflowTriggerService] 🔔 Triggering lead:pipeline_changed workflow',
          {
            leadId: payload.leadId,
            fromPipelineId: payload.fromPipelineId,
            toPipelineId: payload.toPipelineId,
          }
        );

        await workflowExecutionService.triggerWorkflows({
          entityType: 'lead',
          triggerType: 'lead_pipeline_changed',
          entityId: payload.leadId,
          triggerConfig: {
            from_pipeline_id: payload.fromPipelineId,
            to_pipeline_id: payload.toPipelineId,
          },
          triggerData: {
            business_name: payload.businessName,
          },
        });
      } catch (error) {
        console.error(
          '[WorkflowTriggerService] ❌ Error in lead:pipeline_changed handler:',
          error
        );
      }
    });

    // Lead Updated
    eventBus.onEvent('lead:updated', async (payload) => {
      try {
        console.log('[WorkflowTriggerService] 🔔 Triggering lead:updated workflow', {
          leadId: payload.leadId,
          fieldsChanged: payload.fieldsChanged,
        });

        await workflowExecutionService.triggerWorkflows({
          entityType: 'lead',
          triggerType: 'lead_updated',
          entityId: payload.leadId,
          triggerConfig: {
            pipeline_id: payload.pipelineId,
            fields_changed: payload.fieldsChanged,
          },
          triggerData: {
            business_name: payload.businessName,
          },
        });
      } catch (error) {
        console.error(
          '[WorkflowTriggerService] ❌ Error in lead:updated handler:',
          error
        );
      }
    });

    // Lead Converted to Client
    eventBus.onEvent('lead:converted_to_client', async (payload) => {
      try {
        console.log(
          '[WorkflowTriggerService] 🔔 Triggering lead:converted_to_client workflow',
          {
            leadId: payload.leadId,
            clientId: payload.clientId,
          }
        );

        await workflowExecutionService.triggerWorkflows({
          entityType: 'lead',
          triggerType: 'lead_converted_to_client',
          entityId: payload.leadId,
          triggerData: {
            business_name: payload.businessName,
            client_id: payload.clientId,
          },
        });
      } catch (error) {
        console.error(
          '[WorkflowTriggerService] ❌ Error in lead:converted_to_client handler:',
          error
        );
      }
    });

    // Lead Note Created
    eventBus.onEvent('lead:note_created', async (payload) => {
      try {
        console.log(
          '[WorkflowTriggerService] 🔔 Triggering lead:note_created workflow',
          {
            leadId: payload.leadId,
            noteId: payload.noteId,
          }
        );

        await workflowExecutionService.triggerWorkflows({
          entityType: 'lead',
          triggerType: 'lead_note_created',
          entityId: payload.leadId,
          triggerConfig: {
            pipeline_id: payload.pipelineId,
            stato_cliente: payload.statoCliente,
          },
          triggerData: {
            business_name: payload.businessName,
            note_id: payload.noteId,
            note_content: payload.noteContent,
          },
        });
      } catch (error) {
        console.error(
          '[WorkflowTriggerService] ❌ Error in lead:note_created handler:',
          error
        );
      }
    });

    // Lead Activity Created
    eventBus.onEvent('lead:activity_created', async (payload) => {
      try {
        console.log(
          '[WorkflowTriggerService] 🔔 Triggering lead:activity_created workflow',
          {
            leadId: payload.leadId,
            activityId: payload.activityId,
            activityType: payload.activityType,
          }
        );

        await workflowExecutionService.triggerWorkflows({
          entityType: 'lead',
          triggerType: 'lead_activity_created',
          entityId: payload.leadId,
          triggerConfig: {
            pipeline_id: payload.pipelineId,
            activity_type: payload.activityType,
          },
          triggerData: {
            business_name: payload.businessName,
            activity_id: payload.activityId,
            activity_title: payload.activityTitle,
            activity_type: payload.activityType,
            activity_date: payload.dueDate,
            assigned_to: payload.assignedTo,
          },
        });
      } catch (error) {
        console.error(
          '[WorkflowTriggerService] ❌ Error in lead:activity_created handler:',
          error
        );
      }
    });
  }

  // ============================================================
  // TAG & INTEREST EVENT HANDLERS (shared lead/client)
  // ============================================================

  private registerTagAndInterestEvents() {
    // Tag Added (lead + client)
    for (const prefix of ['lead', 'client'] as const) {
      eventBus.onEvent(`${prefix}:tag_added` as any, async (payload: any) => {
        try {
          console.log(`[WorkflowTriggerService] 🏷️ Triggering ${prefix}:tag_added workflow`, { clientId: payload.clientId, tagId: payload.tagId });
          await workflowExecutionService.triggerWorkflows({
            entityType: prefix,
            triggerType: `${prefix}_tag_added` as any,
            entityId: payload.clientId,
            triggerConfig: { tag_ids: [payload.tagId] },
            triggerData: {
              tag_id: payload.tagId,
              business_name: payload.businessName,
              contact_name: payload.contactName,
              email: payload.email,
              phone: payload.phone,
            },
          });
        } catch (error) {
          console.error(`[WorkflowTriggerService] ❌ Error in ${prefix}:tag_added handler:`, error);
        }
      });

      eventBus.onEvent(`${prefix}:tag_removed` as any, async (payload: any) => {
        try {
          console.log(`[WorkflowTriggerService] 🏷️ Triggering ${prefix}:tag_removed workflow`, { clientId: payload.clientId, tagId: payload.tagId });
          await workflowExecutionService.triggerWorkflows({
            entityType: prefix,
            triggerType: `${prefix}_tag_removed` as any,
            entityId: payload.clientId,
            triggerConfig: { tag_ids: [payload.tagId] },
            triggerData: {
              tag_id: payload.tagId,
              business_name: payload.businessName,
              contact_name: payload.contactName,
              email: payload.email,
              phone: payload.phone,
            },
          });
        } catch (error) {
          console.error(`[WorkflowTriggerService] ❌ Error in ${prefix}:tag_removed handler:`, error);
        }
      });

      eventBus.onEvent(`${prefix}:interest_added` as any, async (payload: any) => {
        try {
          console.log(`[WorkflowTriggerService] 💡 Triggering ${prefix}:interest_added workflow`, { clientId: payload.clientId, interestCategoryId: payload.interestCategoryId });
          await workflowExecutionService.triggerWorkflows({
            entityType: prefix,
            triggerType: `${prefix}_interest_added` as any,
            entityId: payload.clientId,
            triggerConfig: { interest_category_ids: [payload.interestCategoryId] },
            triggerData: {
              interest_category_id: payload.interestCategoryId,
              business_name: payload.businessName,
              contact_name: payload.contactName,
              email: payload.email,
              phone: payload.phone,
            },
          });
        } catch (error) {
          console.error(`[WorkflowTriggerService] ❌ Error in ${prefix}:interest_added handler:`, error);
        }
      });

      eventBus.onEvent(`${prefix}:interest_removed` as any, async (payload: any) => {
        try {
          console.log(`[WorkflowTriggerService] 💡 Triggering ${prefix}:interest_removed workflow`, { clientId: payload.clientId, interestCategoryId: payload.interestCategoryId });
          await workflowExecutionService.triggerWorkflows({
            entityType: prefix,
            triggerType: `${prefix}_interest_removed` as any,
            entityId: payload.clientId,
            triggerConfig: { interest_category_ids: [payload.interestCategoryId] },
            triggerData: {
              interest_category_id: payload.interestCategoryId,
              business_name: payload.businessName,
              contact_name: payload.contactName,
              email: payload.email,
              phone: payload.phone,
            },
          });
        } catch (error) {
          console.error(`[WorkflowTriggerService] ❌ Error in ${prefix}:interest_removed handler:`, error);
        }
      });
    }
  }

  // ============================================================
  // CROSS-MODULE EVENT HANDLERS (project, task, ticket, booking, invoice)
  // These trigger client-type workflows since all these entities link to a client
  // ============================================================

  private registerCrossModuleEvents() {
    // Project Created
    eventBus.onEvent('project:created' as any, async (payload: any) => {
      try {
        if (!payload.clientId) return; // Skip if no client linked
        console.log('[WorkflowTriggerService] 📁 Triggering project:created workflow');
        await workflowExecutionService.triggerWorkflows({
          entityType: 'client',
          triggerType: 'project_created' as any,
          entityId: payload.clientId,
          triggerData: { project_id: payload.projectId, project_name: payload.projectName, project_status: payload.status, project_manager_id: payload.projectManagerId },
        });
      } catch (error) { console.error('[WorkflowTriggerService] ❌ Error in project:created handler:', error); }
    });

    // Task Created
    eventBus.onEvent('task:created' as any, async (payload: any) => {
      try {
        if (!payload.clientId) return;
        console.log('[WorkflowTriggerService] ✅ Triggering task:created workflow');
        await workflowExecutionService.triggerWorkflows({
          entityType: 'client',
          triggerType: 'task_created' as any,
          entityId: payload.clientId,
          triggerData: { task_id: payload.taskId, task_title: payload.taskTitle, task_status: payload.taskStatus, project_id: payload.projectId, project_name: payload.projectName, assigned_to: payload.assignedTo },
        });
      } catch (error) { console.error('[WorkflowTriggerService] ❌ Error in task:created handler:', error); }
    });

    // Task Status Changed (already emitted by projectStore)
    eventBus.onEvent('task:status_changed' as any, async (payload: any) => {
      try {
        if (!payload.projectId) return;
        console.log('[WorkflowTriggerService] 🔄 Triggering task:status_changed workflow');
        // Fetch client_id from project
        const { data: project } = await supabase
          .from('projects')
          .select('client_id')
          .eq('id', payload.projectId)
          .single();
        if (!project?.client_id) return;
        await workflowExecutionService.triggerWorkflows({
          entityType: 'client',
          triggerType: 'task_status_changed' as any,
          entityId: project.client_id,
          triggerData: { task_id: payload.taskId, task_title: payload.taskTitle, from_status: payload.fromStatus, to_status: payload.toStatus, project_id: payload.projectId, project_name: payload.projectName },
        });
      } catch (error) { console.error('[WorkflowTriggerService] ❌ Error in task:status_changed handler:', error); }
    });

    // Ticket Created
    eventBus.onEvent('ticket:created' as any, async (payload: any) => {
      try {
        if (!payload.clientId) return;
        console.log('[WorkflowTriggerService] 🎫 Triggering ticket:created workflow');
        await workflowExecutionService.triggerWorkflows({
          entityType: 'client',
          triggerType: 'ticket_created' as any,
          entityId: payload.clientId,
          triggerData: { ticket_id: payload.ticketId, ticket_title: payload.ticketTitle, ticket_status: payload.ticketStatus, ticket_priority: payload.ticketPriority },
        });
      } catch (error) { console.error('[WorkflowTriggerService] ❌ Error in ticket:created handler:', error); }
    });

    // Booking Created
    eventBus.onEvent('booking:created' as any, async (payload: any) => {
      try {
        if (!payload.clientId) return;
        console.log('[WorkflowTriggerService] 📅 Triggering booking:created workflow');
        await workflowExecutionService.triggerWorkflows({
          entityType: 'client',
          triggerType: 'booking_created' as any,
          entityId: payload.clientId,
          triggerData: { booking_id: payload.bookingId, start_time: payload.startTime, booker_name: payload.bookerName, booker_email: payload.bookerEmail },
        });
      } catch (error) { console.error('[WorkflowTriggerService] ❌ Error in booking:created handler:', error); }
    });

    // Invoice Created
    eventBus.onEvent('invoice:created' as any, async (payload: any) => {
      try {
        console.log('[WorkflowTriggerService] 🧾 Triggering invoice:created workflow');
        // Fetch client_id from deal
        if (!payload.dealId) return;
        const { data: deal } = await supabase
          .from('deals')
          .select('client_id')
          .eq('id', payload.dealId)
          .single();
        if (!deal?.client_id) return;
        await workflowExecutionService.triggerWorkflows({
          entityType: 'client',
          triggerType: 'invoice_created' as any,
          entityId: deal.client_id,
          triggerData: { invoice_id: payload.invoiceId, invoice_amount: payload.amount, invoice_status: payload.status, deal_id: payload.dealId },
        });
      } catch (error) { console.error('[WorkflowTriggerService] ❌ Error in invoice:created handler:', error); }
    });
  }

  // ============================================================
  // CLIENT EVENT HANDLERS
  // ============================================================

  private registerClientEvents() {
    // Client Created
    eventBus.onEvent('client:created', async (payload) => {
      try {
        console.log('[WorkflowTriggerService] 🔔 Triggering client:created workflow', {
          clientId: payload.clientId,
        });

        await workflowExecutionService.triggerWorkflows({
          entityType: 'client',
          triggerType: 'client_created',
          entityId: payload.clientId,
          triggerData: {
            business_name: payload.businessName,
            stato_cliente: payload.statoCliente,
          },
        });
      } catch (error) {
        console.error(
          '[WorkflowTriggerService] ❌ Error in client:created handler:',
          error
        );
      }
    });

    // Client Updated
    eventBus.onEvent('client:updated', async (payload) => {
      try {
        console.log('[WorkflowTriggerService] 🔔 Triggering client:updated workflow', {
          clientId: payload.clientId,
          fieldsChanged: payload.fieldsChanged,
        });

        await workflowExecutionService.triggerWorkflows({
          entityType: 'client',
          triggerType: 'client_updated',
          entityId: payload.clientId,
          triggerConfig: {
            fields_changed: payload.fieldsChanged,
          },
          triggerData: {
            business_name: payload.businessName,
          },
        });
      } catch (error) {
        console.error(
          '[WorkflowTriggerService] ❌ Error in client:updated handler:',
          error
        );
      }
    });

    // Client State Changed
    eventBus.onEvent('client:state_changed', async (payload) => {
      try {
        console.log(
          '[WorkflowTriggerService] 🔔 Triggering client:state_changed workflow',
          {
            clientId: payload.clientId,
            fromStateId: payload.fromStateId,
            toStateId: payload.toStateId,
          }
        );

        await workflowExecutionService.triggerWorkflows({
          entityType: 'client',
          triggerType: 'client_state_changed',
          entityId: payload.clientId,
          triggerConfig: {
            from_state_id: payload.fromStateId,
            to_state_id: payload.toStateId,
          },
          triggerData: {
            business_name: payload.businessName,
          },
        });
      } catch (error) {
        console.error(
          '[WorkflowTriggerService] ❌ Error in client:state_changed handler:',
          error
        );
      }
    });

    // Client Note Created
    eventBus.onEvent('client:note_created', async (payload) => {
      try {
        console.log(
          '[WorkflowTriggerService] 🔔 Triggering client:note_created workflow',
          {
            clientId: payload.clientId,
            noteId: payload.noteId,
          }
        );

        await workflowExecutionService.triggerWorkflows({
          entityType: 'client',
          triggerType: 'client_note_created',
          entityId: payload.clientId,
          triggerData: {
            business_name: payload.businessName,
            note_id: payload.noteId,
            note_content: payload.noteContent,
          },
        });
      } catch (error) {
        console.error(
          '[WorkflowTriggerService] ❌ Error in client:note_created handler:',
          error
        );
      }
    });

    // Client Activity Created
    eventBus.onEvent('client:activity_created', async (payload) => {
      try {
        console.log(
          '[WorkflowTriggerService] 🔔 Triggering client:activity_created workflow',
          {
            clientId: payload.clientId,
            activityId: payload.activityId,
            activityType: payload.activityType,
          }
        );

        await workflowExecutionService.triggerWorkflows({
          entityType: 'client',
          triggerType: 'client_activity_created',
          entityId: payload.clientId,
          triggerConfig: {
            activity_type: payload.activityType,
          },
          triggerData: {
            business_name: payload.businessName,
            activity_id: payload.activityId,
            activity_title: payload.activityTitle,
            activity_type: payload.activityType,
            activity_date: payload.dueDate,
            assigned_to: payload.assignedTo,
          },
        });
      } catch (error) {
        console.error(
          '[WorkflowTriggerService] ❌ Error in client:activity_created handler:',
          error
        );
      }
    });
  }

  // ============================================================
  // DEAL EVENT HANDLERS
  // ============================================================

  private registerDealEvents() {
    // Deal Created
    eventBus.onEvent('deal:created', async (payload) => {
      try {
        console.log('[WorkflowTriggerService] 🔔 Triggering deal:created workflow', {
          dealId: payload.dealId,
          clientId: payload.clientId,
          leadId: payload.leadId,
        });

        await workflowExecutionService.triggerWorkflows({
          entityType: 'deal',
          triggerType: 'deal_created',
          entityId: payload.dealId,
          triggerData: {
            client_id: payload.clientId,
            lead_id: payload.leadId,
            title: payload.title,
            status: payload.status,
            total_value: payload.totalValue,
          },
        });
      } catch (error) {
        console.error(
          '[WorkflowTriggerService] ❌ Error in deal:created handler:',
          error
        );
      }
    });

    // Deal State Changed
    eventBus.onEvent('deal:state_changed', async (payload) => {
      try {
        console.log(
          '[WorkflowTriggerService] 🔔 Triggering deal:state_changed workflow',
          {
            dealId: payload.dealId,
            fromStatus: payload.fromStatus,
            toStatus: payload.toStatus,
          }
        );

        await workflowExecutionService.triggerWorkflows({
          entityType: 'deal',
          triggerType: 'deal_state_changed',
          entityId: payload.dealId,
          triggerConfig: {
            from_status: payload.fromStatus,
            to_status: payload.toStatus,
          },
          triggerData: {
            title: payload.title,
            client_id: payload.clientId,
            lead_id: payload.leadId,
            total_value: payload.totalValue,
          },
        });
      } catch (error) {
        console.error(
          '[WorkflowTriggerService] ❌ Error in deal:state_changed handler:',
          error
        );
      }
    });

    // Deal Signed
    eventBus.onEvent('deal:signed', async (payload) => {
      try {
        console.log('[WorkflowTriggerService] 🔔 Triggering deal:signed workflow', {
          dealId: payload.dealId,
          totalValue: payload.totalValue,
        });

        await workflowExecutionService.triggerWorkflows({
          entityType: 'deal',
          triggerType: 'deal_signed',
          entityId: payload.dealId,
          triggerData: {
            title: payload.title,
            total_value: payload.totalValue,
            client_id: payload.clientId,
            signed_at: payload.signedAt,
          },
        });
      } catch (error) {
        console.error(
          '[WorkflowTriggerService] ❌ Error in deal:signed handler:',
          error
        );
      }
    });

    // Deal Assigned
    eventBus.onEvent('deal:assigned', async (payload) => {
      try {
        console.log('[WorkflowTriggerService] 🔔 Triggering deal:assigned workflow', {
          dealId: payload.dealId,
          fromUserId: payload.fromUserId,
          toUserId: payload.toUserId,
        });

        await workflowExecutionService.triggerWorkflows({
          entityType: 'deal',
          triggerType: 'deal_assigned',
          entityId: payload.dealId,
          triggerConfig: {
            from_user_id: payload.fromUserId,
            to_user_id: payload.toUserId,
          },
          triggerData: {
            title: payload.title,
          },
        });
      } catch (error) {
        console.error(
          '[WorkflowTriggerService] ❌ Error in deal:assigned handler:',
          error
        );
      }
    });

    // Deal Updated
    eventBus.onEvent('deal:updated', async (payload) => {
      try {
        console.log('[WorkflowTriggerService] 🔔 Triggering deal:updated workflow', {
          dealId: payload.dealId,
          fieldsChanged: payload.fieldsChanged,
        });

        await workflowExecutionService.triggerWorkflows({
          entityType: 'deal',
          triggerType: 'deal_updated',
          entityId: payload.dealId,
          triggerConfig: {
            fields_changed: payload.fieldsChanged,
          },
          triggerData: {
            title: payload.title,
          },
        });
      } catch (error) {
        console.error(
          '[WorkflowTriggerService] ❌ Error in deal:updated handler:',
          error
        );
      }
    });

    // Deal Note Created
    eventBus.onEvent('deal:note_created', async (payload) => {
      try {
        console.log(
          '[WorkflowTriggerService] 🔔 Triggering deal:note_created workflow',
          {
            dealId: payload.dealId,
            noteId: payload.noteId,
          }
        );

        await workflowExecutionService.triggerWorkflows({
          entityType: 'deal',
          triggerType: 'deal_note_created',
          entityId: payload.dealId,
          triggerData: {
            title: payload.title,
            note_id: payload.noteId,
            note_content: payload.noteContent,
          },
        });
      } catch (error) {
        console.error(
          '[WorkflowTriggerService] ❌ Error in deal:note_created handler:',
          error
        );
      }
    });

    // Deal Activity Created
    eventBus.onEvent('deal:activity_created', async (payload) => {
      try {
        console.log(
          '[WorkflowTriggerService] 🔔 Triggering deal:activity_created workflow',
          {
            dealId: payload.dealId,
            activityId: payload.activityId,
            activityType: payload.activityType,
          }
        );

        await workflowExecutionService.triggerWorkflows({
          entityType: 'deal',
          triggerType: 'deal_activity_created',
          entityId: payload.dealId,
          triggerConfig: {
            activity_type: payload.activityType,
          },
          triggerData: {
            title: payload.title,
            activity_id: payload.activityId,
            activity_title: payload.title,
            activity_type: payload.activityType,
            activity_date: payload.dueDate,
            client_id: payload.clientId,
            lead_id: payload.leadId,
            assigned_to: payload.assignedTo,
            due_date: payload.dueDate,
          },
        });
      } catch (error) {
        console.error(
          '[WorkflowTriggerService] ❌ Error in deal:activity_created handler:',
          error
        );
      }
    });
  }

  // ============================================================
  // NEWSLETTER EVENT HANDLERS
  // ============================================================

  private registerNewsletterEvents() {
    // Newsletter Contact Added to List
    // Triggered by newsletterRealtimeService when a new subscriber is inserted
    eventBus.onEvent('newsletter:contact_added', async (payload) => {
      try {
        console.log(
          '[WorkflowTriggerService] 🔔 Triggering newsletter:contact_added workflow',
          {
            subscriberId: payload.subscriberId,
            listId: payload.listId,
            email: payload.email,
            leadId: payload.leadId,
            clientId: payload.clientId,
          }
        );

        // Validate required fields - subscriberId and listId are required
        // These come from the newsletterRealtimeService when a subscriber record is created
        if (!payload.subscriberId || !payload.listId) {
          console.warn(
            '[WorkflowTriggerService] ⚠️ newsletter:contact_added: Missing subscriberId or listId, skipping',
            { subscriberId: payload.subscriberId, listId: payload.listId }
          );
          return;
        }

        // Use 'newsletter' entity type to match workflows with entity_type='newsletter'
        // The subscriberId is the entity we're enrolling in the workflow
        await workflowExecutionService.triggerWorkflows({
          entityType: 'newsletter',
          triggerType: 'contact_added_to_list',
          entityId: payload.subscriberId,
          triggerConfig: {
            list_id: payload.listId,
          },
          triggerData: {
            list_id: payload.listId,
            list_name: payload.listName,
            subscriber_id: payload.subscriberId,
            email: payload.email,
            first_name: payload.firstName,
            last_name: payload.lastName,
            // Include linked entity info for workflow actions that need it
            lead_id: payload.leadId,
            client_id: payload.clientId,
            subscribed_at: new Date().toISOString(),
          },
        });

        console.log('[WorkflowTriggerService] 🎯 Newsletter workflow triggered:', {
          subscriberId: payload.subscriberId,
          listId: payload.listId,
        });
      } catch (error) {
        console.error(
          '[WorkflowTriggerService] ❌ Error in newsletter:contact_added handler:',
          error
        );
      }
    });
  }
}

// Export singleton instance
export const workflowTriggerService = new WorkflowTriggerService();
